import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useAuth } from './AuthContext';
import { useFeatureGate, ADMIN_UID } from '../context/FeatureGateContext';
import { useOmniAgentPick } from '../context/OmniAgentContext';
import { useTheme } from '../context/ThemeContext';
import { relativeTime, exactTime } from '../utils/formatTime';
import { useConfirm } from '../context/ConfirmContext';
import { usesShell, panelFor } from './Shell/panelRoutes';
import { useGettingStarted } from '../context/GettingStartedContext';
import { showsOn as showsGettingStarted } from './GettingStarted/GettingStartedBar';

const styles = `
  .app-sidebar {
    width: 240px;
    height: 100vh;
    /* On the page's own ground, like the design canvas: the rail is part of
       the page, not a tinted strip floating over it (2026-09-27). */
    background: var(--background-dark);
    display: flex;
    flex-direction: column;
    padding: 0;
    box-shadow: none;
    z-index: 9999;
    flex-shrink: 0;
    border-right: 1px solid var(--border-color);
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
  }

  .app-sidebar.collapsed {
    width: 60px;
  }

  .app-sidebar.collapsed .sidebar-title,
  .app-sidebar.collapsed .nav-section-title,
  .app-sidebar.collapsed .chat-history-section,
  .app-sidebar.collapsed .app-nav-item span:not(.material-symbols-rounded),
  .app-sidebar.collapsed .new-chat-btn span:not(.material-symbols-rounded),
  .app-sidebar.collapsed .user-identity-chip span:not(.material-symbols-rounded) {
    display: none;
  }
  
  .app-sidebar.collapsed .new-chat-btn {
    justify-content: center;
    padding: 10px;
  }

  .app-sidebar.collapsed .app-nav-item {
    justify-content: center;
    padding: 10px;
  }

  .app-sidebar.collapsed .sidebar-header {
    justify-content: center;
    padding: 14px 0 12px 0;
  }

  .app-sidebar.collapsed .user-identity-chip {
    justify-content: center;
    padding: 8px 0;
  }

  .app-sidebar.collapsed .sidebar-scroll-area {
    padding: 0 8px;
  }

  .app-sidebar.collapsed .sidebar-footer {
    padding: 14px 8px;
  }

  .sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 14px 12px 14px;
  }

  .sidebar-logo-container {
    display: flex;
    align-items: center;
    gap: 10px;
    text-decoration: none;
    color: var(--text-heading);
  }

  .sidebar-logo {
    width: 26px;
    height: 26px;
    border-radius: 7px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }

  .sidebar-title {
    font-weight: 700;
    font-size: 0.95rem;
    letter-spacing: -0.01em;
  }

  .new-chat-btn {
    background: var(--background-light);
    border: 1px solid var(--border-color);
    color: var(--text-heading);
    cursor: pointer;
    padding: 7px 12px;
    border-radius: 8px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 600;
    font-size: 0.85rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
    width: 100%;
    margin-bottom: 12px;
  }

  .new-chat-btn:hover {
    background: var(--background-dark);
    border-color: var(--border-color);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  }
  
  .sidebar-collapse-btn {
    background: transparent;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    padding: 6px;
    border-radius: 6px;
    transition: all 0.2s ease;
  }

  .sidebar-collapse-btn:hover {
    background: var(--hover-background);
    color: var(--primary-color);
  }

  .sidebar-scroll-area {
    flex: 1;
    overflow-y: auto;
    padding: 0 10px;
    display: flex;
    flex-direction: column;
    gap: 18px;
  }
  
  .sidebar-scroll-area::-webkit-scrollbar {
    width: 4px;
  }
  .sidebar-scroll-area::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 4px;
  }

  .nav-section {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .nav-section-title {
    font-size: 0.68rem;
    font-weight: 700;
    color: var(--text-subtle);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0 10px;
    margin-bottom: 6px;
  }

  .app-nav-item {
    color: var(--text-default);
    text-decoration: none;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    border-radius: 8px;
    font-size: 0.85rem;
    font-weight: 500;
  }

  .app-nav-item:hover {
    color: var(--text-heading);
    background: var(--hover-background);
  }

  /* Where you are, said quietly: a soft teal tint and ink, as the design
     canvas draws it. The gradient with a glow shouted louder than the page. */
  .app-nav-item.active {
    color: var(--text-heading) !important;
    background: color-mix(in srgb, var(--primary-color) 11%, transparent);
    box-shadow: none;
    font-weight: 600;
  }

  /* Get started, hidden: a row like the others that brings it back. */
  button.app-nav-item {
    width: 100%;
    border: 0;
    background: transparent;
    font-family: inherit;
    text-align: left;
    cursor: pointer;
  }
  .nav-get-started { color: var(--primary-color); font-weight: 600; }

  .chat-history-item {
    color: var(--text-default);
    padding: 6px 10px;
    border-radius: 8px;
    font-size: 0.82rem;
    cursor: pointer;
    transition: all 0.2s ease;
    /* Truncation now lives on the inner title span (.text-truncate) instead
       of here — this row also hosts the kebab-menu dropdown, and overflow:
       hidden at this level was clipping that menu before it could ever
       appear (on top of the sidebar's scroll container doing the same). */
  }

  .chat-history-item:hover {
    background: rgba(0, 0, 0, 0.05);
    color: var(--text-heading);
  }

  .chat-history-item.active {
    background: var(--hover-background);
    color: var(--primary-color);
    font-weight: 600;
  }

  /* Kebab menu for each Recent Chat row: hidden until the row is hovered (or
     the menu itself is open), so the default state matches a plain chat list
     rather than every row showing a delete icon. */
  .chat-item-menu-toggle {
    background: transparent;
    border: none;
    color: inherit;
    opacity: 0;
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    padding: 0;
    transition: opacity 0.15s ease, background 0.15s ease;
  }

  .chat-item-menu-toggle::after {
    display: none;
  }

  .chat-history-item:hover .chat-item-menu-toggle,
  .chat-item-menu-toggle[aria-expanded="true"] {
    opacity: 0.7;
  }

  .chat-item-menu-toggle:hover,
  .chat-item-menu-toggle[aria-expanded="true"] {
    background: rgba(0, 0, 0, 0.08);
    opacity: 1;
  }

  .app-sidebar.collapsed .chat-item-menu-toggle {
    display: none;
  }

  .sidebar-footer {
    padding: 12px;
    border-top: 1px solid var(--border-color);
  }

  .user-identity-chip {
    width: 100%;
    padding: 7px 8px;
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s ease;
    color: var(--text-default);
    border: none;
  }

  .user-identity-chip:hover {
    background: var(--hover-background);
    color: var(--primary-color);
  }

  /* Mobile Top Bar + Hamburger Menu */
  .mobile-top-bar {
    display: none;
  }

  @media (max-width: 768px) {
    .mobile-top-bar {
      display: flex;
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: var(--mobile-header-h, 48px);
      background: transparent;
      border: none;
      border-bottom: none;
      z-index: 10000;
      align-items: center;
      justify-content: flex-start;
      padding: 0 12px;
      box-shadow: none;
      pointer-events: none;
    }

    body.dark-theme .mobile-top-bar {
      background: transparent;
      border: none;
      border-bottom: none;
      box-shadow: none;
    }
    /* Pages that do not put their own controls in the strip get a solid
       bar with the logo; the public pages get the same with the sign-in
       link at the right. */
    .mobile-top-bar--solid,
    body.dark-theme .mobile-top-bar--solid {
      background: var(--background-light);
      border-bottom: 1px solid var(--border-color);
      pointer-events: auto;
    }
    .mobile-top-bar--public {
      justify-content: space-between;
      gap: 8px;
      padding: 0 max(12px, env(safe-area-inset-right)) 0 max(12px, env(safe-area-inset-left));
    }
    /* A visitor's one bar (2026-09-30). The landing page's own nav is hidden
       at this width (LandingPage.css), so this carries what that nav offers
       on a computer: Pricing, Log in and Get started, beside the logo and the
       theme switch. Every target is 44px tall. */
    .public-bar-logo {
      display: flex;
      flex: none;
      align-items: center;
      gap: 8px;
      min-width: 44px;
      min-height: 44px;
      font-weight: 700;
      color: var(--text-heading);
      text-decoration: none;
    }
    .public-bar-links {
      display: flex;
      align-items: center;
      gap: 2px;
      min-width: 0;
    }
    .mobile-top-bar--public .lp-theme-toggle {
      display: flex;
      flex: none;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      padding: 0;
      border: 0;
      border-radius: 999px;
      background: transparent;
      color: var(--text-heading);
      cursor: pointer;
    }
    .public-bar-link {
      display: inline-flex;
      align-items: center;
      min-height: 44px;
      padding: 0 8px;
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--text-heading);
      text-decoration: none;
      white-space: nowrap;
    }
    .public-bar-link.active { color: var(--primary-color); }
    .public-bar-cta {
      display: inline-flex;
      flex: none;
      align-items: center;
      min-height: 44px;
      margin-left: 4px;
      text-decoration: none;
    }
    .public-bar-cta > span {
      display: inline-flex;
      align-items: center;
      height: 36px;
      padding: 0 14px;
      border-radius: 999px;
      background: var(--primary-color);
      color: var(--on-primary);
      font-size: 0.88rem;
      font-weight: 600;
      white-space: nowrap;
    }
    .mobile-hamburger {
      background: transparent;
      border: none;
      color: var(--text-heading);
      opacity: 0.7;
      padding: 8px;
      border-radius: 8px;
      min-width: 44px;
      min-height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      pointer-events: auto;
      transition: opacity 0.15s ease, background 0.15s ease;
    }
    .mobile-hamburger:hover,
    .mobile-hamburger:active {
      opacity: 1;
      background: var(--hover-background);
    }

    .app-sidebar {
      position: fixed;
      top: 0;
      left: 0;
      /* vh first as the fallback, then dvh: 100vh on mobile is the viewport
         with the URL bar hidden, so the drawer runs taller than the screen and
         its last item sits under the browser chrome. */
      height: 100vh;
      height: 100dvh;
      width: 280px;
      max-width: 85vw;
      z-index: 10001;
      transform: translateX(-100%);
      /* visibility, not just transform. A drawer moved offscreen is still in
         the tab order and still read by a screen reader, so a mobile user
         tabbing from the top bar lands inside an invisible menu with no way to
         tell where they are. Delayed so it does not blank the panel mid-slide
         on the way out. */
      visibility: hidden;
      transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), visibility 0s linear 0.3s;
      box-shadow: 10px 0 30px rgba(0,0,0,0.1);
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }
    .app-sidebar.mobile-open {
      transform: translateX(0);
      visibility: visible;
      transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), visibility 0s;
    }

    /* Keep normal vertical layout for the sidebar itself, just hide the collapse button */
    .sidebar-collapse-btn {
      display: none;
    }
    
    .mobile-overlay {
      position: fixed;
      /* inset rather than width:100vw — 100vw counts the scrollbar gutter, so
         it overflows the page and produces a horizontal scrollbar of its own. */
      inset: 0;
      background: rgba(15, 23, 22, 0.4);
      z-index: 10000;
      opacity: 0;
      pointer-events: none;
      /* Closed, it is not drawn at all (2026-10-31): it was an invisible,
         full-screen blur layer over every page on a phone, which the browser
         may composite on every scroll and tap. Hidden once its fade ends. */
      visibility: hidden;
      transition: opacity 0.3s ease, visibility 0s linear 0.3s;
    }
    .mobile-overlay.mobile-open {
      opacity: 1;
      pointer-events: auto;
      visibility: visible;
      backdrop-filter: blur(2px);
      -webkit-backdrop-filter: blur(2px);
      transition: opacity 0.3s ease, visibility 0s;
    }

    /* The drawer's rows are a thumb's targets, 44px tall (2026-09-30). */
    .app-nav-item,
    .new-chat-btn,
    .user-identity-chip,
    .chat-history-item {
      min-height: 44px;
    }
  }

  /* The narrowest phones: the logo's mark alone, then the theme switch
     steps aside before any of the three links does. */
  @media (max-width: 400px) {
    .public-bar-name { display: none; }
  }
  @media (max-width: 350px) {
    .mobile-top-bar--public .lp-theme-toggle { display: none; }
  }

  /* A touch screen has no hover: each recent chat's ⋯ is there all the
     time, at a size a thumb can hit (2026-09-30). */
  @media (hover: none) {
    .chat-item-menu-toggle {
      opacity: 0.7;
      width: 40px;
      height: 40px;
    }
  }

  /* A drawer that flies in is exactly the kind of movement this setting is
     for. Honour it: the menu still opens and closes, it just does not animate. */
  @media (prefers-reduced-motion: reduce) {
    .app-sidebar,
    .mobile-overlay {
      transition: none;
    }
  }
`;

// In the order a job search happens, and by how often each screen is opened
// (founder, 2026-09-05: "set the pages in sequence"). The feed is where a
// person lands and returns every day; applying sits directly under it,
// because that is where somebody realises they are about to type the same
// answers for the fifth time; tracking follows applying. The resume studio
// and the interview coach are the tools behind those three, and Admin is
// last and only for admins. One Jobs entry, because there is one screen —
// "For You" and "Find Jobs" were two rankings of one catalogue that
// disagreed with each other.
// Named as the panel beside the chat names them (founder's decision,
// 2026-09-27: Jobs · Applications · Resume), with the same icons.
const navLinks = [
  { to: "/for-you", icon: "work", label: "Jobs" },
  { to: "/job-agent", icon: "send", label: "Applications" },
  { to: "/design-studio", icon: "description", label: "Resume" },
  { to: "/about-you", icon: "person", label: "About you" },
  { to: "/tracking", icon: "stacked_bar_chart", label: "Tracker", flag: "JOB_TRACKER" },
  { to: "/interview-coach", icon: "forum", label: "Interview coach" },
  { to: "/admin", icon: "admin_panel_settings", label: "Admin", adminOnly: true },
];

const NavItem = ({ to, icon, label }) => (
  <NavLink to={to} className="app-nav-item">
    <span className="material-symbols-rounded" style={{ fontSize: '1.2rem', width: '20px', display: 'flex', justifyContent: 'center' }}>{icon}</span>
    <span>{label}</span>
  </NavLink>
);

// One past chat in the rail: its title and age, and its menu (pin, rename,
// link, delete). Its own memoised component (2026-10-31): the rail rendered
// every chat again — each with a dozen menu items and a formatted date — on
// every change of page and, before that, on every streamed word of a reply.
// The menu is built when first opened, not with the row.
const SessionRow = React.memo(function SessionRow({
  session, active, renaming, setRenamingChat, onOpen,
  pinSession, renameSession, linkSessionWorkspace, deleteSession, confirm,
}) {
  const linkedNav = navLinks.find(l => l.to === session.linkedWorkspace);
  return (
    <div
      className={`chat-history-item d-flex justify-content-between align-items-center ${active ? 'active' : ''}`}
    >
      <div
        className="flex-grow-1 text-truncate d-flex align-items-center gap-1"
        style={{ cursor: 'pointer', minWidth: 0 }}
        onClick={() => onOpen(session)}
      >
        {session.isPinned && (
          <span className="material-symbols-rounded opacity-75 flex-shrink-0" style={{ fontSize: '1.1rem' }} title="Pinned">push_pin</span>
        )}
        <span className="text-truncate">{session.title}</span>
        {/* Whether this chat is from an hour ago or last month.
            The list is ordered by updatedAt already; it just
            never said so. */}
        {session.updatedAt && (
          <span className="ag-session-time" title={exactTime(session.updatedAt)}>
            {relativeTime(session.updatedAt)}
          </span>
        )}
        {linkedNav && (
          <span className="material-symbols-rounded opacity-50 flex-shrink-0" style={{ fontSize: '1.1rem' }} title={`Linked to ${linkedNav.label}`}>{linkedNav.icon}</span>
        )}
      </div>

      <Dropdown align="end" autoClose="outside">
        <Dropdown.Toggle
          as="button"
          bsPrefix="chat-item-menu-toggle"
          title="Chat options"
        >
          <span className="material-symbols-rounded" style={{ fontSize: '1.2rem' }}>more_vert</span>
        </Dropdown.Toggle>

        <Dropdown.Menu
          className="shadow-lg border-0 rounded-3"
          style={{ minWidth: '200px', zIndex: 1200 }}
          popperConfig={{ strategy: 'fixed' }}
        >
          <Dropdown.Item
            onClick={() => pinSession(session.id, !session.isPinned)}
            className="py-2 d-flex align-items-center gap-2"
          >
            <span className="material-symbols-rounded text-muted" style={{ fontSize: '1.1rem', width: '20px', display: 'flex', justifyContent: 'center' }}>push_pin</span>
            {session.isPinned ? 'Unpin' : 'Pin'}
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => {
              setRenamingChat({ id: session.id, title: session.title || '' });
            }}
            className="py-2 d-flex align-items-center gap-2"
          >
            <span className="material-symbols-rounded text-muted" style={{ fontSize: '1.1rem', width: '20px', display: 'flex', justifyContent: 'center' }}>edit_square</span>
            Rename
          </Dropdown.Item>
          {renaming && (
            <form className="px-3 py-2" onClick={e => e.stopPropagation()} onSubmit={e => { e.preventDefault(); if (renaming.title.trim()) { renameSession(session.id, renaming.title.trim()); setRenamingChat(null); } }}>
              <input autoFocus aria-label="Chat title" value={renaming.title} onChange={e => setRenamingChat({ ...renaming, title: e.target.value })} onKeyDown={e => { if (e.key === 'Escape') setRenamingChat(null); }} />
              <button type="submit">Save</button>
            </form>
          )}

          <Dropdown.Divider />
          <Dropdown.Header className="text-uppercase fw-bold text-muted d-flex align-items-center gap-1" style={{ fontSize: '0.65rem', letterSpacing: '0.03em' }}>
            <span className="material-symbols-rounded" style={{ fontSize: '0.9rem' }}>link</span>
            Link for reference
          </Dropdown.Header>
          {navLinks
            .filter(l => !l.adminOnly)
            .map(l => (
              <Dropdown.Item
                key={l.to}
                active={session.linkedWorkspace === l.to}
                onClick={() => linkSessionWorkspace(session.id, session.linkedWorkspace === l.to ? null : l.to)}
                className="py-2 d-flex align-items-center gap-2"
              >
                <span className={`material-symbols-rounded ${session.linkedWorkspace === l.to ? '' : 'text-muted'}`} style={{ fontSize: '1.1rem', width: '20px', display: 'flex', justifyContent: 'center' }}>{l.icon}</span>
                {l.label}
              </Dropdown.Item>
            ))}

          <Dropdown.Divider />
          <Dropdown.Item
            onClick={async () => {
              const yes = await confirm({
                title: 'Delete this chat?',
                body: `"${session.title}" and its messages will be removed. This cannot be undone.`,
                confirmLabel: 'Delete chat', icon: 'delete',
              });
              if (yes) {
                deleteSession(session.id);
              }
            }}
            className="py-2 d-flex align-items-center gap-2 text-danger"
          >
            <span className="material-symbols-rounded" style={{ fontSize: '1.1rem', width: '20px', display: 'flex', justifyContent: 'center' }}>delete</span>
            Delete
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
});

const Navigation = ({ ownsStrip = false, isPublic = false }) => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isFeatureEnabled } = useFeatureGate();
  const [renamingChat, setRenamingChat] = useState(null);
  const {
    sessions,
    activeSessionId,
    loadSession,
    startNewSession,
    deleteSession,
    pinSession,
    renameSession,
    linkSessionWorkspace
  } = useOmniAgentPick('sessions', 'activeSessionId', 'loadSession', 'startNewSession', 'deleteSession', 'pinSession', 'renameSession', 'linkSessionWorkspace');
  const { isDark, toggleTheme } = useTheme();
  // Folded or open as this person last left it (a per-viewer convenience:
  // blocked storage just starts open).
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try { return window.localStorage.getItem('aa.rail.folded') === '1'; } catch { return false; }
  });
  const setFolded = (folded) => {
    setIsCollapsed(folded);
    try { window.localStorage.setItem('aa.rail.folded', folded ? '1' : '0'); } catch { /* storage blocked */ }
  };
  // Beside the chat (Shell/ChatShell.js) a conversation loads where it is,
  // and a page opening in the panel folds the rail to icons, as the design
  // has it. Opening it again is the person's call, and it stays open.
  const { pathname } = useLocation();
  const inShell = usesShell({ pathname, width: window.innerWidth, signedIn: Boolean(currentUser) });
  // A page of its own (Chats) is not a panel beside the chat: the rail stays
  // open there, and a chat picked from it opens on the home.
  const onPage = inShell && Boolean(panelFor(pathname)?.page);
  const panelOpen = inShell && Boolean(panelFor(pathname)) && !onPage;
  useEffect(() => { if (panelOpen) setIsCollapsed(true); }, [panelOpen]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const userEmail = currentUser?.email || "No email";

  // One way out (2026-09-29): /logout clears the preview and query caches
  // and the server's session cookie, then signs out of Firebase. This menu
  // ran its own copy that skipped two of those and went to /login.
  const handleLogout = () => navigate('/logout');

  const closeMobileMenu = () => setMobileMenuOpen(false);
  // Opens a past chat. Stable, so the rail's rows are not drawn again for a
  // change of page; it reads where the person is when pressed.
  const where = useRef({});
  where.current = { inShell, onPage, navigate, loadSession };
  const openSession = useCallback((session) => {
    const { inShell, onPage, navigate, loadSession } = where.current;
    loadSession(session.id);
    // Beside the chat the conversation opens where it is, with the page
    // left as it was. Elsewhere, or on a page of its own (Chats), there is
    // no chat on screen, so it opens on the home.
    if (!inShell || onPage) navigate('/dashboard');
    setMobileMenuOpen(false);
  }, []);
  const gettingStarted = useGettingStarted();

  // Any change of page closes the drawer (2026-09-30). Each link closed it by
  // hand, and the ones that did not (Account Settings in the user menu) left
  // it open over the page they had just opened.
  useEffect(() => { setMobileMenuOpen(false); }, [pathname]);

  // Escape closes it, and the page behind it stops scrolling while it is open.
  //
  // Both are things a drawer is expected to do and neither happens for free.
  // Without the scroll lock the content behind the overlay still scrolls under
  // your finger on iOS, which reads as the drawer being broken rather than as
  // the page moving. Without Escape there is no keyboard way out at all — the
  // backdrop is a div, and clicking it is the only close affordance.
  useEffect(() => {
    if (!mobileMenuOpen) return undefined;

    const onKeyDown = (e) => { if (e.key === 'Escape') setMobileMenuOpen(false); };
    document.addEventListener('keydown', onKeyDown);

    // Restore whatever was there rather than assuming '' — another component
    // may legitimately be holding a lock of its own (a modal above this one).
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previous;
    };
  }, [mobileMenuOpen]);

  if (isPublic) {
    return (
      <>
        <style>{styles}</style>
        {/* A phone's one bar on every public page, the landing page too:
            what its nav offers on a computer (Pricing, Log in, Get started),
            the logo home and the theme switch (2026-09-30). */}
        <div className="mobile-top-bar mobile-top-bar--solid mobile-top-bar--public">
          <Link to="/" className="public-bar-logo" aria-label="AssistAlien">
            <img src={`${process.env.PUBLIC_URL}/logo-64.png`} alt="" width="24" height="24" className="rounded-circle" />
            <span className="public-bar-name">AssistAlien</span>
          </Link>
          <nav className="public-bar-links" aria-label="Site">
            <button type="button" className="lp-theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
              <span className="material-symbols-rounded" aria-hidden="true">{isDark ? 'light_mode' : 'dark_mode'}</span>
            </button>
            <NavLink to="/pricing" className="public-bar-link">Pricing</NavLink>
            <NavLink to="/login" className="public-bar-link">Log in</NavLink>
            <Link to="/register" className="public-bar-cta"><span>Get started</span></Link>
          </nav>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      
      {/* Mobile Top Bar */}
      {/* Phones only: the base rule hides this bar, and the phone media
          query shows it. Never set `display` inline here — an inline value
          wins over the desktop's display:none and the bar lands in the
          desktop layout as a flex item. The solid look on pages that do not
          own the strip is a class for the same reason. */}
      <div className={`mobile-top-bar${ownsStrip ? '' : ' mobile-top-bar--solid'}`}>
        {/* aria-label because the button's only content is an icon ligature,
            which a screen reader announces as the bare word "menu" with no
            indication that it is a control or what state it is in. */}
        <button
          className="mobile-hamburger"
          onClick={() => setMobileMenuOpen(true)}
          aria-label="Open navigation menu"
          aria-expanded={mobileMenuOpen}
          aria-controls="app-sidebar"
        >
          <span className="material-symbols-rounded" aria-hidden="true" style={{ fontSize: '1.5rem', display: 'flex' }}>menu</span>
        </button>

        {!ownsStrip && (
          <div style={{ marginLeft: '12px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: 'var(--text-heading)' }}>
            <img src={`${process.env.PUBLIC_URL}/logo-64.png`} alt="" width="24" height="24" className="rounded-circle" />
            <span>AssistAlien</span>
          </div>
        )}
      </div>

      {/* Mobile Overlay */}
      {/* Decorative backdrop. Tapping it closes, but it is deliberately not a
          focusable control — the keyboard route out is Escape, handled above,
          and a focusable div here would just add a stop that announces nothing. */}
      <div
        className={`mobile-overlay ${mobileMenuOpen ? 'mobile-open' : ''}`}
        onClick={closeMobileMenu}
        aria-hidden="true"
      />

      <aside id="app-sidebar" className={`app-sidebar ${isCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        {/* Header: Logo & Collapse Button */}
        <div className="sidebar-header">
          {!isCollapsed ? (
            <>
              <NavLink to="/" className="sidebar-logo-container" onClick={closeMobileMenu}>
                <img
                  src={`${process.env.PUBLIC_URL}/logo-64.png`}
                  alt="Logo"
                  className="sidebar-logo"
                />
                <span className="sidebar-title">AssistAlien</span>
              </NavLink>
              
              <button
                className="sidebar-collapse-btn"
                onClick={() => setFolded(true)}
                title="Collapse Sidebar"
              >
                <span 
                  className="material-symbols-rounded" 
                  style={{ fontSize: '1.2rem', display: 'flex' }}
                >
                  left_panel_close
                </span>
              </button>
            </>
          ) : (
            <div 
              className="sidebar-logo-container" 
              style={{ cursor: 'pointer' }}
              onClick={() => setFolded(false)}
              title="Expand Sidebar"
            >
              <img
                src={`${process.env.PUBLIC_URL}/logo-64.png`}
                alt="Logo"
                className="sidebar-logo"
              />
            </div>
          )}
        </div>

        <div className="sidebar-scroll-area">
          <button
            className="new-chat-btn"
            onClick={() => {
              // The AI lives on Dashboard now — a full embedded chat page,
              // not an overlay popped open in place. See Dashboard.js.
              startNewSession();
              navigate('/dashboard');
              closeMobileMenu();
            }}
          >
            <span>New Chat</span>
            <span className={`material-symbols-rounded ${isCollapsed ? '' : 'text-muted'}`} style={{ fontSize: '1.1rem' }}>edit_square</span>
          </button>

          {/* Every conversation, and a search across them (founder,
              2026-09-28), right under New Chat as in Claude. */}
          <div onClick={closeMobileMenu}>
            <NavItem to="/chats" icon="chat_bubble" label="Chats" />
          </div>

          {/* Get started, hidden with its ×: the way back to it, until the
              steps are done (founder, 2026-09-28). */}
          {gettingStarted.loaded && gettingStarted.hidden && !gettingStarted.complete && (
            <button
              type="button"
              className="app-nav-item nav-get-started"
              onClick={() => {
                gettingStarted.show();
                closeMobileMenu();
                if (!showsGettingStarted(pathname)) navigate('/dashboard');
              }}
            >
              <span className="material-symbols-rounded" style={{ fontSize: '1.2rem', width: '20px', display: 'flex', justifyContent: 'center' }}>flag</span>
              <span>Get started · {gettingStarted.steps.filter(s => !s.done).length} left</span>
            </button>
          )}

          {/* Main Navigation */}
          <div className="nav-section">
            <div className="nav-section-title">Workspace</div>
            {navLinks
              .filter(link => {
                if (link.adminOnly) return currentUser?.uid === ADMIN_UID;
                if (link.flag) return isFeatureEnabled(link.flag);
                return true;
              })
              .map((link) => (
                <div key={link.label} onClick={closeMobileMenu}>
                  <NavItem {...link} />
                </div>
              ))}
          </div>

          {/* Chat History */}
          <div className="nav-section mt-2 chat-history-section">
            <div className="nav-section-title">Recent Chats</div>
            {/* Defensive (2026-08): sessions is normally always an array
                (OmniAgentContext now validates its own fetch), but this
                component renders on EVERY route via the app shell — a
                second guard here means a future bad state anywhere else
                can't take down the whole app the way this one did. */}
            {!Array.isArray(sessions) || sessions.length === 0 ? (
              <div className="text-muted small px-3">No recent chats</div>
            ) : (
              sessions.map(session => (
                <SessionRow
                  key={session.id}
                  session={session}
                  active={activeSessionId === session.id}
                  renaming={renamingChat?.id === session.id ? renamingChat : null}
                  setRenamingChat={setRenamingChat}
                  onOpen={openSession}
                  pinSession={pinSession}
                  renameSession={renameSession}
                  linkSessionWorkspace={linkSessionWorkspace}
                  deleteSession={deleteSession}
                  confirm={confirm}
                />
              ))
            )}
          </div>
        </div>

        {/* User Menu Footer */}
        {currentUser && (
          <div className="sidebar-footer">
            <Dropdown drop="up">
              <Dropdown.Toggle as="div" className="user-identity-chip">
                <span className="material-symbols-rounded" style={{ fontSize: '1.4rem' }}>account_circle</span>
                <span className="fw-600 text-truncate" style={{ fontSize: '0.85rem', maxWidth: '140px' }}>
                  {currentUser.displayName || userEmail}
                </span>
              </Dropdown.Toggle>

              <Dropdown.Menu className="shadow-lg border-0 rounded-3 mb-2" style={{ minWidth: '220px' }}>
                <div className="px-3 py-2 border-bottom mb-2">
                  <div className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>{currentUser.displayName || 'User'}</div>
                  <div className="text-muted text-truncate" style={{ fontSize: '0.8rem' }}>{userEmail}</div>
                </div>
                
                <Dropdown.Item onClick={() => { closeMobileMenu(); navigate('/account'); }} className="py-2 d-flex align-items-center gap-2">
                  <span className="material-symbols-rounded text-secondary" style={{ fontSize: '1.2rem', width: '20px', display: 'flex', justifyContent: 'center' }}>person</span>
                  Account Settings
                </Dropdown.Item>

                {/* Dark mode has one home when signed in: Account → Appearance
                    (2026-09-29). The signed-out header keeps its toggle,
                    since visitors cannot reach Account. */}

                <Dropdown.Divider />
                
                <Dropdown.Item onClick={handleLogout} className="py-2 text-danger d-flex align-items-center gap-2">
                  <span className="material-symbols-rounded" style={{ fontSize: '1.2rem', width: '20px', display: 'flex', justifyContent: 'center' }}>logout</span>
                  Sign Out
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}
      </aside>
    </>
  );
};

export default Navigation;
